import React, { Fragment, useCallback } from 'react';
import PropTypes from 'prop-types';
import { alpha, styled } from '@mui/material';
import { get } from '@totalsoft/change-tracking-react';
import { getErrors, isValid } from '@totalsoft/pure-validations-react';
import { emptyString } from 'utils/constants';
import { TextField, IconButton } from '@totalsoft/rocket-ui';
import FlexBox from 'components/common/FlexBox';
import { useTranslation } from 'react-i18next';
import { StyledTableBodyCell, StyledTableRow } from 'components/common/Styles';
import { ReportOutlined } from '@mui/icons-material';

export const StyledTableRowSelected = styled(StyledTableRow)(({ theme }) => ({
  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08) }
}));

const PartnerTypeComponent = ({
  key,
  lens,
  disabled,
  index,
  validation,
  onChangeProperty,
  onSelectedPartner,
  onSelectedPartnerValidation,
  onEdit,
  onDelete,
  onCancelAdd,
  navigateToDetailsArea
}) => {
  const [t] = useTranslation();
  const isNew = typeof (lens.id |> get) === 'string';

  const handleCancelEdit = useCallback(
    (id, event) => {
      onCancelAdd(id);
      onSelectedPartner(null);
      event.stopPropagation();
    },
    [onCancelAdd, onSelectedPartner]
  );

  const handleDelete = useCallback(
    (id, event) => {
      onDelete(id);
      onSelectedPartner(null);
      event.stopPropagation();
    },
    [onDelete, onSelectedPartner]
  );

  const handleNavigateToDetailsArea = useCallback(
    event => {
      onSelectedPartner(lens |> get);
      navigateToDetailsArea();
      event.stopPropagation();
    },
    [navigateToDetailsArea, onSelectedPartner, lens]
  );

  const getErrorMessage = useCallback(() => {
    const errorMessages = [];
    const fieldErrors = [
      { field: t('PartnerType.Details.PartnerBaseType.Title'), validation: validation.baseTypes },
      { field: t('PartnerType.Code'), validation: validation.code },
      { field: t('PartnerType.Name'), validation: validation.name }
    ];

    for (const { field, validation } of fieldErrors) {
      if (!isValid(validation)) {
        let errors = getErrors(validation);
        if (!Array.isArray(errors)) {
          errors = [errors];
        }

        errorMessages.push(`${field}: ${errors.join(', ')}`);
      }
    }
    return errorMessages.join(', ');
  }, [validation, t]);

  return (
    <Fragment>
      <StyledTableRowSelected
        key={key}
        onClick={() => {
          onSelectedPartner(lens |> get);
          onSelectedPartnerValidation(index);
        }}
      >
        {lens.editing |> get ? (
          <Fragment>
            <StyledTableBodyCell
              onClick={() => {
                onSelectedPartner(lens |> get);
                onSelectedPartnerValidation(index);
              }}
            >
              <TextField
                required
                fullWidth
                value={(lens.code |> get) || emptyString}
                onChange={value => onChangeProperty('code', value, lens)}
                error={!isValid(validation.code)}
                helperText={getErrors(validation.code)}
                disabled={disabled || !isNew}
              />
            </StyledTableBodyCell>
            <StyledTableBodyCell
              onClick={() => {
                onSelectedPartner(lens |> get);
                onSelectedPartnerValidation(index);
              }}
            >
              <TextField
                required
                fullWidth
                value={(lens.name |> get) || emptyString}
                onChange={value => onChangeProperty('name', value, lens)}
                error={!isValid(validation.name)}
                helperText={getErrors(validation.name)}
                disabled={disabled}
              />
            </StyledTableBodyCell>
            <StyledTableBodyCell>
              <FlexBox gap={1} alignItems='center' justifyContent='end'>
                {!isValid(validation) && (
                  <IconButton size='small' variant='text' color='error' tooltip={getErrorMessage()} disabled={disabled}>
                    <ReportOutlined />
                  </IconButton>
                )}
                <IconButton
                  size='small'
                  type='cancel'
                  variant='text'
                  color='primary'
                  onClick={e => handleCancelEdit(lens.id |> get, e)}
                  tooltip={t('Cancel')}
                  disabled={disabled}
                />
              </FlexBox>
            </StyledTableBodyCell>
          </Fragment>
        ) : (
          <Fragment>
            <StyledTableBodyCell>{lens.code |> get}</StyledTableBodyCell>
            <StyledTableBodyCell>{lens.name |> get}</StyledTableBodyCell>
            <StyledTableBodyCell>
              <FlexBox gap={1} alignItems='center' justifyContent='end'>
                <IconButton
                  size='small'
                  type='view'
                  variant='text'
                  color='primary'
                  onClick={e => handleNavigateToDetailsArea(e)}
                  tooltip={t('Details')}
                  disabled={disabled}
                />
                <IconButton
                  size='small'
                  type='edit'
                  variant='text'
                  color='primary'
                  onClick={() => onEdit(lens.id |> get)}
                  tooltip={t('Edit')}
                  disabled={disabled}
                />
                <IconButton
                  size='small'
                  type='delete'
                  variant='text'
                  color='primary'
                  onClick={e => handleDelete(lens.id |> get, e)}
                  tooltip={t('Delete')}
                  disabled={disabled}
                />
              </FlexBox>
            </StyledTableBodyCell>
          </Fragment>
        )}
      </StyledTableRowSelected>
    </Fragment>
  );
};

PartnerTypeComponent.propTypes = {
  key: PropTypes.number,
  lens: PropTypes.object.isRequired,
  disabled: PropTypes.bool.isRequired,
  index: PropTypes.number,
  onChangeProperty: PropTypes.func,
  validation: PropTypes.object.isRequired,
  onSelectedPartner: PropTypes.func.isRequired,
  onSelectedPartnerValidation: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCancelAdd: PropTypes.func.isRequired,
  navigateToDetailsArea: PropTypes.func
};
export default PartnerTypeComponent;
